import { useReactiveVar } from '@apollo/client';

import { theme } from 'aimo-ui';
import PaymentCardContent from 'components/account/paymentMethods/PaymentCardContent';
import VehicleAdd from 'components/account/vehicle/VehicleAdd';
import VehicleCameraRecognition from 'components/account/vehicle/VehicleCameraRecognition';
import {
  OnboardingAddPaymentMethod,
  OnboardingAddVehicle,
  OnboardingWelcome,
  PushNotifications,
} from 'components/onboarding';
import AdyenCardAddContainer from 'components/payment/AdyenCardAddContainer';
import CreditCardForm from 'components/payment/CreditCardForm';
import { useAdyenCardAddEnabled } from 'hooks/useAdyenCardAddEnabled';
import useOnboardingRedirect from 'hooks/useOnboardingRedirect';
import ModalStackHeader, {
  ModalStackHeaderCustomProps,
} from 'navigation/ModalStackHeader';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePaymentStore } from 'stores/paymentStore';
import { MainWrapper } from 'styles/ModalStackContainerStyles';
import { createStackNavigator } from 'utils/navigationHelpers';
import { getModalOptions, transitionOptions } from 'utils/navigationUtils';
import { resolveAddCardScreenTitle } from 'utils/paymentUtils';

const OnboardingStack = createStackNavigator();

// Exported for the render-site flag-branch test (Task 7 part 2). Not used as a
// named import anywhere in app code — the navigator references it directly below.
export const AddCardOnboardingScreen = () => {
  const {
    handleSuccessNavigationAfterAddCard,
    handleFailureNavigationAfterAddCard,
  } = useOnboardingRedirect();
  // Adyen card-add provider gate (flag + app country + email; dark by default,
  // no Nets fallback once true). Shared across the entry hook, the add-payment
  // modal and onboarding so all gate identically. When true the onboarding
  // add-card screen runs the Adyen flow, mapping the container's terminal handoff
  // onto the existing Nets onboarding nav: success -> handleSuccessNavigation...,
  // close -> handleFailureNavigation... Otherwise the Nets CreditCardForm is
  // untouched. Nav-away unmounts the container; the stashed session stays (every
  // open re-inits it) and a pre-submit unmount sends the abandon cancel.
  const useAdyen = useAdyenCardAddEnabled();
  return (
    <MainWrapper>
      {useAdyen ? (
        <AdyenCardAddContainer
          onSuccess={handleSuccessNavigationAfterAddCard}
          onClose={handleFailureNavigationAfterAddCard}
        />
      ) : (
        <CreditCardForm
          afterSuccess={handleSuccessNavigationAfterAddCard}
          afterFailure={handleFailureNavigationAfterAddCard}
        />
      )}
    </MainWrapper>
  );
};

const AddCardDetailsOnboardingScreen = () => {
  const { handleSuccessNavigationAfterAddCardDetails } =
    useOnboardingRedirect();
  return (
    <MainWrapper>
      <PaymentCardContent
        handleDone={handleSuccessNavigationAfterAddCardDetails}
      />
    </MainWrapper>
  );
};

const ModalHeaderCloseComponent = (props: ModalStackHeaderCustomProps) => (
  <ModalStackHeader {...props} closeButton={true} closeButtonType="secondary" />
);

const ModalHeaderBackComponent = (props: ModalStackHeaderCustomProps) => (
  <ModalStackHeader {...props} backButtonType="secondary" />
);

const OnboardingNavigator: React.FC = () => {
  const { t } = useTranslation();
  const modalOptions = useMemo(() => {
    return {
      ...getModalOptions(),
      small: getModalOptions('25%', false),
    };
  }, []);
  const paymentCard = usePaymentStore((s) => s.selectedPaymentCard);
  const addCardWalletType = usePaymentStore(
    (s) => s.adyenCardAddSession?.walletType
  );
  const { handleSuccessNavigationAfterAddCardDetails } =
    useOnboardingRedirect();

  return (
    <OnboardingStack.Navigator
      screenOptions={{
        ...transitionOptions(),
        cardStyle: { backgroundColor: theme.colors.white },
        header: (props) => <ModalStackHeader {...props} />,
      }}>
      <OnboardingStack.Group
        screenOptions={{
          cardStyle: {
            backgroundColor: theme.colors.lightBg.orange,
          },
        }}>
        <OnboardingStack.Screen
          name="welcome"
          component={OnboardingWelcome}
          options={{
            title: t('onboarding.welcome'),
            headerShown: false,
            cardStyle: {
              backgroundColor: theme.colors.lightBg.pink,
            },
          }}
        />
        <OnboardingStack.Screen
          name="pushNotifications"
          component={PushNotifications}
          options={{
            title: t('onboarding.pushNotifications'),
            headerShown: false,
            cardStyle: { backgroundColor: theme.colors.lightBg.pink },
          }}
        />
      </OnboardingStack.Group>
      <OnboardingStack.Screen
        name="onboardingContinue"
        component={OnboardingAddVehicle}
        options={{
          title: t('onboarding.continue'),
          headerShown: false,
          cardStyle: { backgroundColor: theme.colors.lightBg.pink },
        }}
      />
      <OnboardingStack.Screen
        name="addVehicleOnboarding"
        component={VehicleAdd}
        options={{
          title: t('onboarding.addVehicle'),
          header: (props) =>
            ModalHeaderCloseComponent({
              ...props,
              ...modalOptions.headerStyle,
            }),
          cardStyle: { ...modalOptions.cardStyle, marginTop: 0 },
        }}
      />
      <OnboardingStack.Screen
        name="vehicleCameraRecognitionOnboarding"
        component={VehicleCameraRecognition}
        options={{
          title: t('vehicle.cameraRecognition.title'),
          header: (props) =>
            ModalHeaderBackComponent({ ...props, ...modalOptions.headerStyle }),
          cardStyle: { ...modalOptions.cardStyle, marginTop: 0 },
        }}
      />
      <OnboardingStack.Screen
        name="addPaymentMethod"
        component={OnboardingAddPaymentMethod}
        options={{
          title: t('onboarding.addPaymentMethod'),
          headerShown: false,
          cardStyle: { backgroundColor: theme.colors.lightBg.pink },
        }}
      />
      <OnboardingStack.Screen
        name="addCardOnboarding"
        component={AddCardOnboardingScreen}
        options={{
          presentation: 'modal',
          title: resolveAddCardScreenTitle(
            t,
            paymentCard.status,
            addCardWalletType
          ),
          header: (props) =>
            ModalHeaderCloseComponent({
              ...props,
              ...modalOptions.headerStyle,
              onClose:
                paymentCard.status === 'Verifying'
                  ? handleSuccessNavigationAfterAddCardDetails
                  : undefined,
            }),
          cardOverlayEnabled: true,
          cardStyle:
            paymentCard.status === 'Verifying'
              ? modalOptions.small.cardStyle
              : { ...modalOptions.cardStyle, marginTop: 0 },
        }}
      />
      <OnboardingStack.Screen
        name="addCardDetailsOnboarding"
        component={AddCardDetailsOnboardingScreen}
        options={{
          presentation: 'transparentModal',
          title: `${paymentCard.title} ${paymentCard.subtitle}`,
          header: (props) =>
            ModalHeaderCloseComponent({
              ...props,
              ...modalOptions.headerStyle,
              onClose: handleSuccessNavigationAfterAddCardDetails,
            }),
          cardOverlayEnabled: true,
          cardStyle: { ...modalOptions.cardStyle, marginTop: 200 },
        }}
      />
    </OnboardingStack.Navigator>
  );
};

export default OnboardingNavigator;
